import React, { useEffect, useMemo, useState } from "react";
import { X, Search, Sparkles, Filter, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { categoryLabel, CATEGORIES, categoryMeta } from "@/lib/categories";

/**
 * Full-screen single-product picker used to swap the gift of an existing cagnotte.
 *
 * Smart defaults (option C):
 *  - Pré-filtré sur la **catégorie courante** du produit et un prix dans une
 *    fourchette de **±20 %** du prix actuel.
 *  - Toggle "Voir tout le catalogue" pour relâcher les filtres.
 *
 * Architecture (option B):
 *  - Recherche full-text (nom + description)
 *  - Sélecteur de catégorie (chips horizontaux)
 *  - Grille de cartes visuelles avec image + prix
 *  - Sélection unique avec preview
 */
export default function ProductSwapPicker({
  open,
  onClose,
  onSelect,
  currentProductId,
  currentCategory,
  currentPrice,
}) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState(currentCategory || "all");
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Smart defaults boundaries (±20% of current price)
  const minPrice = currentPrice ? currentPrice * 0.8 : 0;
  const maxPrice = currentPrice ? currentPrice * 1.2 : Infinity;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSearch("");
    setActiveCat(currentCategory || "all");
    setShowAll(false);
    setSelectedId(null);
    api.get("/admin/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [open, currentCategory]);

  const filtered = useMemo(() => {
    return products
      .filter((p) => !p.archived && p.id !== currentProductId)
      .filter((p) => {
        if (search) {
          const q = search.toLowerCase();
          if (!`${p.name} ${p.description || ""}`.toLowerCase().includes(q)) return false;
        }
        if (activeCat !== "all" && p.category !== activeCat) return false;
        if (!showAll && currentPrice) {
          const price = p.final_price ?? p.price ?? 0;
          if (price < minPrice || price > maxPrice) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Highlight items closest to current price first
        if (currentPrice) {
          const da = Math.abs((a.final_price ?? a.price ?? 0) - currentPrice);
          const db = Math.abs((b.final_price ?? b.price ?? 0) - currentPrice);
          return da - db;
        }
        return 0;
      });
  }, [products, search, activeCat, showAll, currentProductId, currentPrice, minPrice, maxPrice]);

  if (!open) return null;

  const selected = filtered.find((p) => p.id === selectedId);
  const priceDiff = selected && currentPrice
    ? ((selected.final_price ?? selected.price ?? 0) - currentPrice)
    : 0;
  const priceDiffPercent = currentPrice ? (priceDiff / currentPrice) * 100 : 0;
  const isBigDiff = Math.abs(priceDiffPercent) > 50;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-stretch sm:items-center justify-center"
      data-testid="product-swap-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-4xl sm:max-h-[92vh] sm:m-4 sm:rounded-3xl flex flex-col">
        {/* Header */}
        <div className="border-b border-[#F2E2EC] px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#D81B8C]">
              Changer de cadeau
            </p>
            <h2 className="font-display text-xl font-black text-[#2A0E3C]">
              Choisissez un nouveau cadeau
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="swap-picker-close"
            className="w-10 h-10 rounded-full bg-[#FCE7F3] text-[#5C4664] hover:text-[#D81B8C] flex items-center justify-center shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 pt-4 pb-3 border-b border-[#F2E2EC] space-y-3 bg-white">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C4664]" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un cadeau…"
              data-testid="swap-picker-search"
              className="pl-9 h-11 rounded-xl border-[#F2E2EC]"
            />
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex gap-2 overflow-x-auto pb-1 min-w-0 flex-1">
              <Chip
                active={activeCat === "all"}
                onClick={() => setActiveCat("all")}
                testid="swap-picker-cat-all"
              >
                Toutes
              </Chip>
              {CATEGORIES.map((c) => (
                <Chip
                  key={c.slug}
                  active={activeCat === c.slug}
                  onClick={() => setActiveCat(c.slug)}
                  testid={`swap-picker-cat-${c.slug}`}
                  color={categoryMeta(c.slug)?.color}
                >
                  {categoryLabel(c.slug, "fr")}
                </Chip>
              ))}
            </div>
            {currentPrice ? (
              <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                data-testid="swap-picker-show-all-toggle"
                className={`inline-flex items-center gap-2 px-3 h-9 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                  showAll
                    ? "bg-[#2A0E3C] text-white"
                    : "bg-[#FFF5FA] text-[#D81B8C] border border-[#FFB3D9]"
                }`}
                title={showAll ? "Revenir aux suggestions pertinentes" : "Élargir à tout le catalogue"}
              >
                {showAll ? <Filter className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                {showAll ? "Tout le catalogue" : `Suggérés (±20 % de $${currentPrice.toFixed(0)})`}
              </button>
            ) : null}
          </div>
        </div>

        {/* Results grid */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-center text-sm text-[#5C4664] py-12">Chargement du catalogue…</p>
          ) : filtered.length === 0 ? (
            <div data-testid="swap-picker-empty" className="text-center py-12">
              <p className="text-sm text-[#5C4664]">
                Aucun cadeau ne correspond à ces critères.
              </p>
              {!showAll && currentPrice && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  data-testid="swap-picker-empty-show-all"
                  className="mt-3 text-sm font-semibold text-[#D81B8C] underline"
                >
                  Élargir à tout le catalogue →
                </button>
              )}
            </div>
          ) : (
            <ul
              data-testid="swap-picker-grid"
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
            >
              {filtered.map((p) => {
                const price = p.final_price ?? p.price ?? 0;
                const isSelected = p.id === selectedId;
                const inRange = currentPrice
                  ? price >= minPrice && price <= maxPrice
                  : true;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      data-testid={`swap-picker-item-${p.id}`}
                      className={`relative w-full text-left bg-white rounded-2xl overflow-hidden transition-all border-2 ${
                        isSelected
                          ? "border-[#D81B8C] shadow-[0_0_0_4px_rgba(216,27,140,0.15)]"
                          : "border-[#F2E2EC] hover:border-[#D81B8C]/40"
                      }`}
                    >
                      <div className="aspect-square w-full bg-[#FCE7F3] overflow-hidden">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#D81B8C] text-white flex items-center justify-center">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                      {!inRange && showAll && currentPrice && (
                        <span className="absolute top-2 left-2 inline-flex bg-[#FF8A00]/90 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
                          Hors fourchette
                        </span>
                      )}
                      <div className="p-3">
                        <div className="text-[9px] font-bold uppercase tracking-widest text-[#5C4664]">
                          {categoryLabel(p.category, "fr")}
                        </div>
                        <h3 className="mt-1 font-display text-xs sm:text-sm font-black text-[#2A0E3C] leading-snug line-clamp-2">
                          {p.name}
                        </h3>
                        <div className="mt-1.5 font-display text-base font-black text-[#D81B8C]">
                          ${price.toFixed(0)}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer with selected preview + confirm */}
        <div className="border-t border-[#F2E2EC] px-5 py-4 bg-white flex items-center justify-between gap-3 flex-wrap">
          {selected ? (
            <div className="text-sm flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-[#FCE7F3] overflow-hidden shrink-0">
                {selected.image_url && (
                  <img src={selected.image_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display font-black text-[#2A0E3C] truncate">{selected.name}</p>
                <p className="text-xs text-[#5C4664]">
                  ${(selected.final_price ?? selected.price).toFixed(0)}
                  {currentPrice ? (
                    <span className={`ml-2 font-semibold ${isBigDiff ? "text-[#D81B8C]" : "text-[#3FA535]"}`}>
                      {priceDiff >= 0 ? "+" : ""}${priceDiff.toFixed(0)} ({priceDiffPercent >= 0 ? "+" : ""}{priceDiffPercent.toFixed(0)} %)
                    </span>
                  ) : null}
                </p>
                {isBigDiff && (
                  <p className="text-[11px] text-[#D81B8C] font-semibold mt-0.5">
                    ⚠ Différence de prix &gt; 50 %. Confirmez avec le groupe avant.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#5C4664]">Sélectionnez un cadeau pour continuer.</p>
          )}
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="swap-picker-cancel"
              className="rounded-full border-[#F2E2EC]"
            >
              Annuler
            </Button>
            <Button
              disabled={!selected}
              onClick={() => { onSelect(selected); onClose(); }}
              data-testid="swap-picker-confirm"
              className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white"
            >
              Choisir ce cadeau
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testid, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
        active
          ? "bg-[#2A0E3C] text-white"
          : "bg-white text-[#2A0E3C] border border-[#F2E2EC] hover:border-[#D81B8C]/40"
      }`}
      style={!active && color ? { color } : undefined}
    >
      {children}
    </button>
  );
}
